import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Loader from "@/components/Loader";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";

export default function AtelierDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [atelier, setAtelier] = useState(null);
  const [slots, setSlots] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", numChildren: 1, children: [{ firstName: "", age: 6 }] });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/ateliers").then(({ data }) => {
      setAtelier(data.find((a) => a.id === id));
    });
    api.get(`/slots/${id}`).then(({ data }) => setSlots(data)).catch(() => setSlots([]));
  }, [id]);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, firstName: user.name?.split(" ")[0] || "", email: user.email || "" }));
  }, [user]);

  const updateChildren = (n) => {
    const next = [];
    for (let i = 0; i < n; i++) next.push(form.children[i] || { firstName: "", age: 6 });
    setForm({ ...form, numChildren: n, children: next });
  };

  const submitReservation = async (e) => {
    e.preventDefault();
    if (!selectedSlot) return toast.error("Sélectionnez un créneau");
    setSubmitting(true);
    try {
      await api.post("/reservations", {
        slotId: selectedSlot.id,
        atelierId: id,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim().toLowerCase(),
        numChildren: parseInt(form.numChildren, 10) || 1,
        children: form.children.slice(0, form.numChildren).map(c => ({ firstName: c.firstName.trim(), age: parseInt(c.age, 10) })),
      });
      toast.success("Réservation confirmée ! Un email de confirmation a été envoyé.");
      nav("/ateliers");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la réservation");
    } finally { setSubmitting(false); }
  };

  if (!atelier) return <Loader />;

  const color = atelier.colorHex || "#A1C861";

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <Link to="/ateliers" className="text-neutral-400 hover:text-white text-sm">← Retour aux ateliers</Link>

      <div className="card-hsg p-6 mt-4">
        <div className="chip" style={{ background: color + "22", color, border: `1px solid ${color}55` }}>{atelier.type}</div>
        <h1 className="text-3xl font-black mt-3">{atelier.title}</h1>
        {atelier.subtitle && <p className="text-neutral-300 mt-1">{atelier.subtitle}</p>}
        <p className="text-neutral-300 mt-4">{atelier.description}</p>
        <div className="mt-4 flex flex-wrap gap-2 text-sm">
          {atelier.location && <span className="chip" style={{ background: "#ffffff10", color: "#ccc", border: "1px solid #ffffff22" }}>📍 {atelier.location}</span>}
          {atelier.age && <span className="chip" style={{ background: "#ffffff10", color: "#ccc", border: "1px solid #ffffff22" }}>👦 {atelier.age}</span>}
          {atelier.time && <span className="chip" style={{ background: "#ffffff10", color: "#ccc", border: "1px solid #ffffff22" }}>⏰ {atelier.time}</span>}
        </div>
      </div>

      {/* Slots + reservation form */}
      <div className="card-hsg p-6 mt-4">
        <h2 className="font-bold text-xl mb-3">🎫 Réservation</h2>
        {slots === null ? <Loader /> : slots.length === 0 ? (
          <p className="text-neutral-400">Pas de réservation en ligne pour cet atelier. Rendez-vous directement au stand !</p>
        ) : (
          <>
            <div className="grid sm:grid-cols-2 gap-2 mb-6">
              {slots.map((s) => {
                const disabled = s.state !== "open" || s.remaining <= 0;
                const stateLabel = { closed: "Fermé", upcoming: "Bientôt", locked: "Verrouillé", open: "Disponible" }[s.state] || s.state;
                const bgSel = selectedSlot?.id === s.id ? "ring-2 ring-[#5BC5F2] bg-neutral-800" : "bg-neutral-900 hover:bg-neutral-800";
                return (
                  <button key={s.id} disabled={disabled} onClick={() => setSelectedSlot(s)} className={`text-left px-4 py-3 rounded-xl border border-neutral-700 disabled:opacity-40 disabled:cursor-not-allowed ${bgSel}`} data-testid={`slot-${s.id}`}>
                    <div className="font-semibold">{s.label || (s.startTime ? new Date(s.startTime).toLocaleString("fr-CH") : "Créneau")}</div>
                    <div className="text-xs text-neutral-400 mt-1">Places : {s.remaining} / {s.capacity} — <span className={s.state === "open" ? "text-[#A1C861]" : "text-neutral-500"}>{stateLabel}</span></div>
                    {s.state === "upcoming" && s.opensAt && (
                      <div className="text-xs text-[#FEC900] mt-1">Ouvre le {new Date(s.opensAt).toLocaleString("fr-CH")}</div>
                    )}
                  </button>
                );
              })}
            </div>
            {selectedSlot && (
              <form onSubmit={submitReservation} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-3">
                  <input required placeholder="Prénom parent" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} className="bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2" data-testid="input-first-name" />
                  <input required placeholder="Nom parent" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} className="bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2" data-testid="input-last-name" />
                </div>
                <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2" data-testid="input-email" />
                <label className="block text-sm text-neutral-300">Nombre d'enfants
                  <input type="number" min="1" max="10" value={form.numChildren} onChange={(e) => updateChildren(Math.max(1, Math.min(10, parseInt(e.target.value, 10) || 1)))} className="mt-1 w-24 bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2" data-testid="input-num-children" />
                </label>
                <div className="space-y-2">
                  {form.children.slice(0, form.numChildren).map((c, i) => (
                    <div key={i} className="grid grid-cols-3 gap-2">
                      <input required placeholder={`Prénom enfant ${i + 1}`} value={c.firstName} onChange={(e) => { const next = [...form.children]; next[i] = { ...c, firstName: e.target.value }; setForm({ ...form, children: next }); }} className="col-span-2 bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2" />
                      <input required type="number" min="0" max="18" value={c.age} onChange={(e) => { const next = [...form.children]; next[i] = { ...c, age: e.target.value }; setForm({ ...form, children: next }); }} className="bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2" placeholder="Âge" />
                    </div>
                  ))}
                </div>
                <button type="submit" disabled={submitting} className="brand-gradient-btn px-6 py-3 rounded-xl" data-testid="submit-reservation">
                  {submitting ? "Envoi…" : `Réserver pour ${form.numChildren} enfant${form.numChildren > 1 ? "s" : ""}`}
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}
